import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const fetchHero = async () => {
  const { data } = await axios.get(`${API}/hero`);
  return data;
};

export const fetchVideos = async ({ page = 1, limit = 12, search = "", year = "", sort = "top_rated", content_type = "", tag = "", mix = false }) => {
  const params = { page, limit, sort };
  if (mix) params.mix = true;
  if (search) params.search = search;
  if (year && year !== "All") params.year = year;
  if (content_type) params.content_type = content_type;
  if (tag) params.tag = tag;
  const { data } = await axios.get(`${API}/videos`, { params });
  return data;
};

export const fetchShorts = async ({ page = 1, limit = 24, genre, audience, seed } = {}) => {
  const params = { page, limit };
  if (genre) params.genre = genre;
  if (audience) params.audience = audience;
  if (seed != null) params.seed = seed;
  const { data } = await axios.get(`${API}/shorts`, { params });
  return data;
};

export const fetchShortsFacets = async ({ genre, audience } = {}) =>
  (await axios.get(`${API}/shorts/facets`, { params: { ...(genre && { genre }), ...(audience && { audience }) } })).data;

export const fetchTrending = async (limit = 15) => {
  const { data } = await axios.get(`${API}/videos/trending`, { params: { limit } });
  return { items: data };
};

// Trailer guides show the clip's own length; features show the official TMDb runtime.
export const displayDuration = (v) => (v?.is_discovery ? v.video_duration : v?.tmdb_runtime || v?.duration_sec);

export const fetchVideo = async (id) => {
  const { data } = await axios.get(`${API}/videos/${id}`);
  return data;
};

export const fetchRetroShuffle = async (limit = 60, category = "all") => {
  const { data } = await axios.get(`${API}/videos/retro-shuffle`, { params: { limit, category } });
  return data;
};

export const fetchRelated = async (id) => {
  const { data } = await axios.get(`${API}/videos/${id}/related`, { params: { limit: 15 } });
  return data;
};

// One shuffle per page visit (0–999 → server caches each order briefly).
export const shuffleSeed = () => Math.floor(Math.random() * 1000);

export const fetchRedirectLink = async (id) => (await axios.get(`${API}/videos/${id}/redirect-link`)).data;

export const fetchTrailer = async (id) => {
  const { data } = await axios.get(`${API}/videos/${id}/trailer`);
  return data;
};

export const fetchPersonalized = async (id, limit = 12) => {
  const { data } = await axios.get(`${API}/videos/personalized`, { params: { video_id: id, limit } });
  return data;
};

export const fetchNewThisWeek = async (limit = 15) => {
  const { data } = await axios.get(`${API}/videos/new-this-week`, { params: { limit } });
  return data;
};

export const fetchLive = async (limit = 24, tags, includeMarathons = false) => {
  const params = { limit };
  if (tags) params.tags = tags;
  if (includeMarathons) params.include_marathons = true;
  const { data } = await axios.get(`${API}/videos/live`, { params });
  return data;
};

export const fetchTicker = async () => {
  const { data } = await axios.get(`${API}/ticker`);
  return data;
};

export const fetchLiveCount = async () => {
  const { data } = await axios.get(`${API}/videos/live/count`);
  return data;
};

export const fetchWatchProviders = async (id, region = "US") => {
  const { data } = await axios.get(`${API}/videos/${id}/providers`, { params: { region } });
  return data;
};

export const fetchAffiliate = async (id) => {
  const { data } = await axios.get(`${API}/videos/${id}/affiliate`);
  return data;
};

// Anonymous per-browser id so the dashboard can count unique visitors.
const visitorId = () => {
  try {
    let id = localStorage.getItem("rv_vid");
    if (!id) {
      id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("rv_vid", id);
    }
    return id;
  } catch {
    return null;
  }
};

// Fire-and-forget affiliate click log (sendBeacon survives the new-tab navigation).
export const trackAffiliateClick = ({ provider, video_id, title } = {}) => {
  const url = `${API}/affiliate/click`;
  let promoter_id = null;
  try {
    const raw = JSON.parse(localStorage.getItem("rv_ref") || "null");
    if (raw && raw.expires > Date.now()) promoter_id = raw.id;
  } catch {
    /* ignore */
  }
  const page_url = window.location.pathname + window.location.search;
  const payload = { provider, video_id, title, page_url, promoter_id, visitor_id: visitorId() };
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: "application/json" }));
      return;
    }
  } catch (e) {
    /* fall through to axios */
  }
  axios.post(url, payload).catch(() => {});
};

export const createHandoff = async (video_id, position) => {
  const { data } = await axios.post(`${API}/handoff/create`, { video_id, position });
  return data;
};

export const getHandoff = async (code) => {
  const { data } = await axios.get(`${API}/handoff/${code}`);
  return data;
};

export const formatDuration = (sec) => {
  if (sec == null) return "0:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const formatViews = (n) => {
  if (n == null) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
};
